import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Insights as InsightsIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getObservability, runObservabilityTraceCheck, updateObservability } from 'api/securityOps';

const borderColor = 'rgba(245, 158, 11, 0.35)';
const cardSx = { border: '1px solid', borderColor, borderRadius: 3, boxShadow: 'none' };
const metricCardSx = {
  ...cardSx,
  background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(255,255,255,0.02))',
  minHeight: 112
};
const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

const parseError = (error, fallback) => {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  return payload.detail || fallback;
};

const AdminObservabilityPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [traceResult, setTraceResult] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    endpoint: '',
    service_name: 'fuzzscan',
    sample_ratio: 1
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await getObservability({ scope: 'global' });
      setSnapshot(data || null);
      const otel = data?.otel || {};
      setForm({
        enabled: Boolean(otel.enabled),
        endpoint: String(otel.endpoint || ''),
        service_name: String(otel.service_name || 'fuzzscan'),
        sample_ratio: Number(otel.sample_ratio ?? 1)
      });
    } catch (err) {
      setError(parseError(err, 'Failed to load observability settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        otel: {
          enabled: Boolean(form.enabled),
          endpoint: String(form.endpoint || '').trim(),
          service_name: String(form.service_name || 'fuzzscan').trim() || 'fuzzscan',
          sample_ratio: Math.max(0, Math.min(1, Number(form.sample_ratio || 0)))
        }
      };
      await updateObservability(payload);
      setSuccess('Observability settings saved.');
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to save observability settings.'));
    } finally {
      setSaving(false);
    }
  };

  const onTraceCheck = async () => {
    setError('');
    setSuccess('');
    try {
      const data = await runObservabilityTraceCheck();
      setTraceResult(data || null);
      setSuccess(data?.detail || 'Trace check completed.');
    } catch (err) {
      setError(parseError(err, 'Failed to run trace check.'));
    }
  };

  const queue = snapshot?.queue || {};
  const kpis = snapshot?.kpis || {};

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <InsightsIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Observability
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Queue health, workload telemetry, and OpenTelemetry exporter settings.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Reload">
                <span>
                  <IconButton onClick={load} disabled={loading || saving} sx={{ border: '1px solid', borderColor }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={onSave} disabled={saving || loading} sx={actionButtonSx}>
                {saving ? 'Saving...' : 'Save OTel'}
              </Button>
              <Button variant="outlined" onClick={onTraceCheck} disabled={loading || saving} sx={{ borderColor: '#f59e0b', color: '#b45309' }}>
                Trace Check
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={metricCardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Scans (24h)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpis.scans_24h ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={metricCardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Findings (24h)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpis.findings_24h ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={metricCardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Workers Online
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#16a34a' }}>
                {queue.workers_online ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={metricCardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Avg Duration (sec)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpis.avg_scan_duration_seconds ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={metricCardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Trace Check
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: traceResult?.instrumented ? '#166534' : '#334155' }}>
                {traceResult ? (traceResult.instrumented ? 'Instrumented' : 'Not Available') : 'Not run'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                OpenTelemetry Config
              </Typography>
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Switch checked={form.enabled} onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))} />
                  <Typography variant="body2">Enable exporter</Typography>
                </Stack>
                <TextField
                  label="Collector endpoint"
                  size="small"
                  fullWidth
                  value={form.endpoint}
                  onChange={(event) => setForm((prev) => ({ ...prev, endpoint: event.target.value }))}
                  placeholder="http://otel-collector:4318/v1/traces"
                />
                <TextField
                  label="Service name"
                  size="small"
                  fullWidth
                  value={form.service_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, service_name: event.target.value }))}
                />
                <TextField
                  label="Sample ratio (0-1)"
                  size="small"
                  type="number"
                  fullWidth
                  value={form.sample_ratio}
                  onChange={(event) => setForm((prev) => ({ ...prev, sample_ratio: event.target.value }))}
                  inputProps={{ step: 0.1, min: 0, max: 1 }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Queue Snapshot
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['active', 'reserved', 'scheduled'].map((key) => (
                    <TableRow key={key}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{key}</TableCell>
                      <TableCell align="right">{queue[key] ?? 0}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>Workers online</TableCell>
                    <TableCell align="right">{queue.workers_online ?? 0}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {queue.error && <Alert severity="warning" sx={{ mt: 1 }}>Celery inspect error: {queue.error}</Alert>}
              {(queue.workers || []).length > 0 && (
                <Table size="small" sx={{ mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Worker</TableCell>
                      <TableCell align="right">Active</TableCell>
                      <TableCell align="right">Reserved</TableCell>
                      <TableCell align="right">Scheduled</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(queue.workers || []).map((worker) => (
                      <TableRow key={worker}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{worker}</TableCell>
                        <TableCell align="right">{queue.active_by_worker?.[worker] || 0}</TableCell>
                        <TableCell align="right">{queue.reserved_by_worker?.[worker] || 0}</TableCell>
                        <TableCell align="right">{queue.scheduled_by_worker?.[worker] || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdminObservabilityPage;
